import StartupPageTitle from '../components/StartupPageTitle';
import { ChartLineUp as PageHeadingIcon } from '@phosphor-icons/react';
/**
 * MetricsDashboard.jsx — Module 03 (Sprint 6).
 *
 * Founder Visibility Score + per-channel follower history.
 * Manual snapshot entry now; schema is ready for OAuth connectors later.
 */
import React, { useEffect, useMemo, useState } from 'react';
import api from '@/api';
import { toast } from 'sonner';
import {
  ChartLineUp, Plus, Trash, X, ArrowsClockwise, ArrowUp, ArrowDown,
  LinkedinLogo, TwitterLogo, InstagramLogo, YoutubeLogo, EnvelopeSimple, Globe, Browser, CaretDown,
} from '@phosphor-icons/react';

const ACCENT = '#10b981';

const PLATFORM_META = {
  linkedin:   { label: 'LinkedIn',   icon: LinkedinLogo,    color: '#0a66c2' },
  twitter:    { label: 'Twitter/X',  icon: TwitterLogo,     color: '#1d9bf0' },
  instagram:  { label: 'Instagram',  icon: InstagramLogo,   color: '#e1306c' },
  youtube:    { label: 'YouTube',    icon: YoutubeLogo,     color: '#ff0000' },
  newsletter: { label: 'Newsletter', icon: EnvelopeSimple,  color: '#a855f7' },
  blog:       { label: 'Blog',       icon: Browser,         color: '#f59e0b' },
  website:    { label: 'Website',    icon: Globe,           color: '#64748b' },
};

const todayKey = () => new Date().toISOString().slice(0, 10);
const fmtDate = (s) => s ? new Date(s).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }) : '';
const fmtNum = (n) => (n || 0).toLocaleString('en-IN');

const MetricsDashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reloadKey, setReloadKey] = useState(0);
  const [addChannelOpen, setAddChannelOpen] = useState(false);
  const [snapshotForChannel, setSnapshotForChannel] = useState(null);
  const reload = () => setReloadKey((k) => k + 1);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get('/metrics/dashboard');
        if (!cancelled) setData(r.data);
      } catch (e) {
        if (!cancelled) toast.error(e.response?.data?.detail || 'Failed to load');
      } finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [reloadKey]);

  const deleteChannel = async (ch) => {
    if (!window.confirm(`Remove ${ch.platform} channel "${ch.handle}" and all its history?`)) return;
    try {
      await api.delete(`/metrics/channels/${ch.id}`);
      toast.success('Channel removed');
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  if (loading) return <div className="p-12 text-sm text-text-muted inline-flex items-center gap-2"><ArrowsClockwise size={14} className="animate-spin" /> Loading…</div>;
  if (!data) return null;

  const vs = data.visibility_score;
  const bandColor = { high: '#10b981', medium: '#f59e0b', low: '#ef4444', minimal: '#64748b' }[vs.band] || '#64748b';

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-[1280px] mx-auto pb-20" data-testid="metrics-dashboard">
      <div className="mb-6 startup-page-header">
        <div className="text-[10px] uppercase tracking-[0.2em] font-bold mb-2" style={{ color: ACCENT }}>Module 03</div>
        <StartupPageTitle icon={PageHeadingIcon} className="text-3xl sm:text-4xl font-heading font-bold text-text-primary leading-[1.05] mb-2" style={{ letterSpacing: '-0.02em' }}>
          Visibility metrics
        </StartupPageTitle>
        <p className="text-sm text-text-secondary">Track followers, engagement, and posting cadence across every channel.</p>
      </div>

      {/* Visibility Score panel */}
      <div className="bg-surface border border-border rounded-2xl p-6 mb-6 grid grid-cols-1 md:grid-cols-[280px_1fr] gap-6 items-center" data-testid="metrics-visibility-score">
        <div className="text-center md:text-left">
          <div className="text-[10px] uppercase tracking-wider font-bold text-text-muted mb-1">Founder Visibility Score</div>
          <div className="flex items-baseline gap-2 justify-center md:justify-start">
            <span className="text-6xl font-mono font-bold" style={{ color: bandColor }} data-testid="visibility-score-value">{vs.score}</span>
            <span className="text-xl font-mono text-text-muted">/100</span>
          </div>
          <div className="text-xs uppercase tracking-wider font-bold mt-1" style={{ color: bandColor }}>{vs.band}</div>
          {vs.total_followers > 0 && <div className="text-[11px] text-text-muted mt-2">{fmtNum(vs.total_followers)} followers across all channels</div>}
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {Object.entries(vs.components).map(([k, v]) => (
            <ScoreComponent key={k} label={k.replace(/_/g, ' ')} value={v} />
          ))}
        </div>
      </div>

      {/* Channels */}
      <div className="flex items-center justify-between mb-3">
        <div className="text-[10px] uppercase tracking-wider font-bold text-text-muted">Channels ({data.channels.length})</div>
        <button onClick={() => setAddChannelOpen(true)} data-testid="metrics-add-channel" className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-lg text-white" style={{ backgroundColor: ACCENT }}>
          <Plus size={11} weight="bold" /> Add channel
        </button>
      </div>

      {data.channels.length === 0 ? (
        <div className="border border-dashed border-border rounded-xl p-10 text-center" data-testid="metrics-empty">
          <ChartLineUp size={28} className="mx-auto mb-2 text-text-muted/40" />
          <p className="text-sm text-text-secondary mb-1">No channels yet</p>
          <p className="text-xs text-text-muted">Add LinkedIn, Twitter/X, Instagram, YouTube, Newsletter, Blog or Website to start tracking.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="metrics-channels">
          {data.channels.map((ch) => (
            <ChannelCard
              key={ch.id}
              channel={ch}
              snapshots={data.snapshots_by_channel[ch.id] || []}
              onAddSnapshot={() => setSnapshotForChannel(ch)}
              onDelete={() => deleteChannel(ch)}
              onSnapshotDeleted={reload}
            />
          ))}
        </div>
      )}

      {addChannelOpen && <AddChannelModal supported={data.supported_platforms} onClose={() => setAddChannelOpen(false)} onSaved={() => { setAddChannelOpen(false); reload(); }} />}
      {snapshotForChannel && <AddSnapshotModal channel={snapshotForChannel} onClose={() => setSnapshotForChannel(null)} onSaved={() => { setSnapshotForChannel(null); reload(); }} />}
    </div>
  );
};

const ScoreComponent = ({ label, value }) => (
  <div className="bg-background border border-border rounded-lg p-3">
    <div className="text-[10px] uppercase tracking-wider font-bold text-text-muted truncate">{label}</div>
    <div className="text-xl font-mono font-bold text-text-primary mt-0.5">{value}<span className="text-xs text-text-muted">/25</span></div>
    <div className="h-1 bg-border rounded-full mt-1.5 overflow-hidden">
      <div className="h-full rounded-full" style={{ width: `${(value / 25) * 100}%`, backgroundColor: ACCENT }} />
    </div>
  </div>
);

const ChannelCard = ({ channel, snapshots, onAddSnapshot, onDelete, onSnapshotDeleted }) => {
  const meta = PLATFORM_META[channel.platform] || { label: channel.platform, icon: Globe, color: '#64748b' };
  const Icon = meta.icon;
  const latest = snapshots[0];
  const previous = snapshots[1];
  const delta = latest && previous ? (latest.followers - previous.followers) : 0;
  const sparkline = useMemo(() => {
    const points = snapshots.slice(0, 12).reverse().map((s) => s.followers || 0);
    if (points.length < 2) return null;
    const max = Math.max(...points); const min = Math.min(...points);
    const range = Math.max(1, max - min);
    return points.map((p, i) => ({ x: (i / (points.length - 1)) * 100, y: 100 - ((p - min) / range) * 100 }));
  }, [snapshots]);

  const removeSnapshot = async (s) => {
    if (!window.confirm('Remove this snapshot?')) return;
    try {
      await api.delete(`/metrics/snapshots/${s.id}`);
      toast.success('Removed');
      onSnapshotDeleted();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  return (
    <div className="bg-surface border border-border rounded-xl p-4" data-testid={`metrics-channel-${channel.id}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2.5">
          <span className="w-8 h-8 rounded-md inline-flex items-center justify-center" style={{ backgroundColor: `${meta.color}1a`, color: meta.color }}>
            <Icon size={15} weight="fill" />
          </span>
          <div>
            <div className="text-sm font-semibold text-text-primary">{meta.label}</div>
            <div className="text-[11px] text-text-muted">@{channel.handle}</div>
          </div>
        </div>
        <button onClick={onDelete} className="text-text-muted hover:text-danger" data-testid={`metrics-channel-delete-${channel.id}`}><Trash size={12} weight="bold" /></button>
      </div>

      {!latest ? (
        <div className="border border-dashed border-border rounded-lg p-4 text-center mb-3">
          <p className="text-xs text-text-muted mb-2">No snapshots yet — add your first one to see trends.</p>
        </div>
      ) : (
        <>
          <div className="flex items-end gap-3 mb-3">
            <div>
              <div className="text-[10px] uppercase tracking-wider font-bold text-text-muted">Followers</div>
              <div className="text-2xl font-mono font-bold text-text-primary">{fmtNum(latest.followers)}</div>
            </div>
            {previous && (
              <div className={`text-xs font-mono font-semibold inline-flex items-center gap-0.5 pb-1.5 ${delta >= 0 ? 'text-success' : 'text-danger'}`}>
                {delta >= 0 ? <ArrowUp size={9} weight="bold" /> : <ArrowDown size={9} weight="bold" />}
                {Math.abs(delta).toLocaleString('en-IN')}
              </div>
            )}
            <div className="ml-auto text-right">
              <div className="text-[10px] uppercase tracking-wider font-bold text-text-muted">Engagement</div>
              <div className="text-sm font-mono font-semibold text-text-primary">{latest.engagement_rate?.toFixed(2) || '0.00'}%</div>
            </div>
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-wider font-bold text-text-muted">Posts/30d</div>
              <div className="text-sm font-mono font-semibold text-text-primary">{latest.posts_last_30 || 0}</div>
            </div>
          </div>

          {sparkline && (
            <div className="h-14 mb-3 -mx-1">
              <svg width="100%" height="56" viewBox="0 0 100 100" preserveAspectRatio="none">
                <polyline
                  points={sparkline.map((p) => `${p.x},${p.y}`).join(' ')}
                  fill="none"
                  stroke={meta.color}
                  strokeWidth="2"
                  vectorEffect="non-scaling-stroke"
                />
              </svg>
            </div>
          )}
        </>
      )}

      <div className="flex items-center justify-between">
        <button onClick={onAddSnapshot} data-testid={`metrics-add-snapshot-${channel.id}`} className="text-xs font-semibold inline-flex items-center gap-1 px-2.5 py-1.5 rounded border border-border hover:border-text-primary">
          <Plus size={11} weight="bold" /> Log snapshot
        </button>
        <div className="text-[10px] text-text-muted">{snapshots.length} snapshot{snapshots.length === 1 ? '' : 's'} on record</div>
      </div>

      {snapshots.length > 0 && (
        <details className="mt-3">
          <summary className="text-[10px] uppercase tracking-wider font-bold text-text-muted cursor-pointer">History</summary>
          <div className="mt-2 space-y-1 max-h-32 overflow-y-auto">
            {snapshots.map((s) => (
              <div key={s.id} className="flex items-center justify-between text-[11px] py-1 border-b border-border/60 last:border-b-0">
                <span className="text-text-muted">{fmtDate(s.captured_at)}</span>
                <span className="font-mono text-text-primary">{fmtNum(s.followers)} · {(s.engagement_rate || 0).toFixed(2)}% · {s.posts_last_30 || 0}p</span>
                <button onClick={() => removeSnapshot(s)} className="text-text-muted hover:text-danger ml-1"><X size={10} weight="bold" /></button>
              </div>
            ))}
          </div>
        </details>
      )}
    </div>
  );
};

const AddChannelModal = ({ supported, onClose, onSaved }) => {
  const [form, setForm] = useState({ platform: supported[0] || 'linkedin', handle: '', display_name: '', url: '' });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!form.handle.trim()) { toast.error('Handle required'); return; }
    setBusy(true);
    try {
      await api.post('/metrics/channels', form);
      toast.success('Channel added');
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };
  return (
    <Modal title="Add channel" onClose={onClose} testid="metrics-add-channel-modal">
      <div className="space-y-3">
        <div>
          <label className="block text-[11px] uppercase tracking-wider font-bold text-text-muted mb-1.5">Platform</label>
          <div className="relative">
            <select value={form.platform} onChange={(e) => setForm((p) => ({ ...p, platform: e.target.value }))} data-testid="metrics-add-channel-platform" className="appearance-none w-full bg-background border border-border rounded-lg pl-3 pr-8 py-2 text-sm">
              {supported.map((p) => <option key={p} value={p}>{(PLATFORM_META[p] || { label: p }).label}</option>)}
            </select>
            <CaretDown size={10} weight="bold" className="absolute right-2 top-1/2 -translate-y-1/2 text-text-muted pointer-events-none" />
          </div>
        </div>
        <div>
          <label className="block text-[11px] uppercase tracking-wider font-bold text-text-muted mb-1.5">Handle / username *</label>
          <input value={form.handle} onChange={(e) => setForm((p) => ({ ...p, handle: e.target.value }))} placeholder="e.g. kitchenos" data-testid="metrics-add-channel-handle" className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm" />
        </div>
        <div>
          <label className="block text-[11px] uppercase tracking-wider font-bold text-text-muted mb-1.5">Display name</label>
          <input value={form.display_name} onChange={(e) => setForm((p) => ({ ...p, display_name: e.target.value }))} className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm" />
        </div>
        <div>
          <label className="block text-[11px] uppercase tracking-wider font-bold text-text-muted mb-1.5">URL</label>
          <input value={form.url} onChange={(e) => setForm((p) => ({ ...p, url: e.target.value }))} placeholder="https://linkedin.com/company/kitchenos" className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm" />
        </div>
      </div>
      <ModalActions onClose={onClose} onSubmit={submit} busy={busy} testid="metrics-add-channel-submit" />
    </Modal>
  );
};

const AddSnapshotModal = ({ channel, onClose, onSaved }) => {
  const meta = PLATFORM_META[channel.platform] || { label: channel.platform };
  const [form, setForm] = useState({ followers: 0, engagement_rate: 0, posts_last_30: 0, captured_at: todayKey() });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      const body = { channel_id: channel.id, followers: Number(form.followers || 0), engagement_rate: Number(form.engagement_rate || 0), posts_last_30: Number(form.posts_last_30 || 0), captured_at: new Date(form.captured_at).toISOString() };
      await api.post('/metrics/snapshots', body);
      toast.success('Snapshot logged');
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };
  return (
    <Modal title={`Log snapshot · ${meta.label} @${channel.handle}`} onClose={onClose} testid="metrics-add-snapshot-modal">
      <div className="space-y-3">
        <Field label="Followers *" type="number" value={form.followers} onChange={(v) => setForm((p) => ({ ...p, followers: v }))} testid="metrics-snapshot-followers" />
        <Field label="Engagement rate (%)" type="number" step="0.01" value={form.engagement_rate} onChange={(v) => setForm((p) => ({ ...p, engagement_rate: v }))} testid="metrics-snapshot-engagement" />
        <Field label="Posts in last 30 days" type="number" value={form.posts_last_30} onChange={(v) => setForm((p) => ({ ...p, posts_last_30: v }))} testid="metrics-snapshot-posts" />
        <Field label="Snapshot date" type="date" value={form.captured_at} onChange={(v) => setForm((p) => ({ ...p, captured_at: v }))} testid="metrics-snapshot-date" />
      </div>
      <ModalActions onClose={onClose} onSubmit={submit} busy={busy} testid="metrics-snapshot-submit" />
    </Modal>
  );
};

/* shared */
const Field = ({ label, value, onChange, type = 'text', step, testid }) => (
  <div>
    <label className="block text-[11px] uppercase tracking-wider font-bold text-text-muted mb-1.5">{label}</label>
    <input type={type} step={step} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-text-primary" />
  </div>
);
const Modal = ({ title, onClose, testid, children }) => (
  <div className="fixed inset-0 bg-black/60 backdrop-blur z-50 flex items-start justify-center pt-16 p-4 overflow-y-auto" onClick={onClose} data-testid={testid}>
    <div className="bg-surface border border-border rounded-xl p-6 w-full max-w-md my-4" onClick={(e) => e.stopPropagation()}>
      <div className="flex items-start justify-between mb-4">
        <h3 className="font-heading font-bold text-text-primary text-lg" style={{ letterSpacing: '-0.01em' }}>{title}</h3>
        <button onClick={onClose} className="text-text-muted hover:text-text-primary"><X size={18} weight="bold" /></button>
      </div>
      {children}
    </div>
  </div>
);
const ModalActions = ({ onClose, onSubmit, busy, testid }) => (
  <div className="flex gap-3 mt-5">
    <button onClick={onClose} className="flex-1 px-4 py-2.5 text-sm border border-border rounded-lg">Cancel</button>
    <button onClick={onSubmit} disabled={busy} data-testid={testid} className="flex-1 px-4 py-2.5 text-sm font-semibold text-white rounded-lg disabled:opacity-50" style={{ backgroundColor: ACCENT }}>
      {busy ? 'Saving…' : 'Save'}
    </button>
  </div>
);

export default MetricsDashboard;
